import { useEffect } from 'react';
import { getHeadingFont, getBodyFont, updateFontConfigFromTheme } from '../config/fontConfig';
import { loadGoogleFonts } from '../utils/fontLoader';
import { getButtonColorCSSVariables, updateButtonColorConfigFromTheme } from '../config/buttonColorConfig';
import { getNavbarColorCSSVariables, updateNavbarColorConfigFromTheme } from '../config/navbarColorConfig';
import { getAlternateSectionColorCSSVariables, updateAlternateSectionColorConfigFromTheme } from '../config/alternateSectionColorConfig';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Hook to initialize and apply font configuration
 * This hook should be called once at the app root level
 *
 * Usage:
 * function App() {
 *   useFontConfig();
 *   return <div>...</div>;
 * }
 */
export const useFontConfig = () => {
  const { themeConfig } = useTheme();

  useEffect(() => {
    // Update all configurations from the fetched theme
    updateFontConfigFromTheme(themeConfig);
    updateButtonColorConfigFromTheme(themeConfig);
    updateNavbarColorConfigFromTheme(themeConfig);
    updateAlternateSectionColorConfigFromTheme(themeConfig);

    // Load Google Fonts if configured
    loadGoogleFonts();

    // Set CSS custom properties for fonts and button colors
    const root = document.documentElement;
    root.style.setProperty('--font-heading', getHeadingFont());
    root.style.setProperty('--font-body', getBodyFont());

    // Apply button colors
    const buttonColors = getButtonColorCSSVariables();
    Object.entries(buttonColors).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Apply navbar colors
    const navbarColors = getNavbarColorCSSVariables();
    Object.entries(navbarColors).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Apply alternate section colors
    const sectionColors = getAlternateSectionColorCSSVariables();
    Object.entries(sectionColors).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Apply banner colors
    root.style.setProperty('--banner-text-color', themeConfig.banner?.textColor || '#8B7355');
    root.style.setProperty('--banner-bg-color', themeConfig.banner?.backgroundColor || '#F5F1E8');

    // Apply font styles to document
    const style = document.createElement('style');
    style.id = 'font-config-styles';
    style.innerHTML = `
      html, body {
        font-family: var(--font-body);
      }

      /* Semantic heading elements */
      h1, h2, h3, h4, h5, h6 {
        font-family: var(--font-heading) !important;
      }

      /* Links, buttons, and interactive elements */
      a, button, [role="button"],
      strong, em b, b, .font-bold, .font-semibold, .font-medium,
      .heading, .banner, .highlight {
        font-family: var(--font-heading) !important;
      }

      /* Collection names, category names, product names */
      .collection-name, .category-name, .product-name,
      .category-title, .product-title, .collection-title {
        font-family: var(--font-heading) !important;
      }

      /* Navbar and menu items */
      nav a, nav button, nav [role="button"],
      [class*="nav"] a, [class*="nav"] button,
      .navbar-menu a, .navbar-menu button,
      .drawer-menu a, .drawer-menu button,
      .mobile-menu a, .mobile-menu button {
        font-family: var(--font-heading) !important;
      }

      /* Blog headings and titles */
      .blog-title, .blog-heading, article h1, article h2,
      .blog-card h3, .blog-slider h3 {
        font-family: var(--font-heading) !important;
      }

      /* Cart and sidebar elements */
      .cart-header, .sidebar-header, .drawer-header,
      [class*="cart"] h1, [class*="cart"] h2, [class*="cart"] h3,
      [class*="sidebar"] h1, [class*="sidebar"] h2, [class*="sidebar"] h3 {
        font-family: var(--font-heading) !important;
      }

      /* Category slider */
      .category-slider h3, .category-slider h4,
      .category-card h3, .category-card h4,
      [class*="category"] h3, [class*="category"] h4 {
        font-family: var(--font-heading) !important;
      }

      /* Product page elements */
      .product-details h1, .product-details h2, .product-details h3,
      .product-header, .product-specification h4,
      [class*="product"] h1, [class*="product"] h2, [class*="product"] h3 {
        font-family: var(--font-heading) !important;
      }

      /* Featured Products section */
      .featured-products h2, .featured-products h3,
      [class*="featured"] h2, [class*="featured"] h3 {
        font-family: var(--font-heading) !important;
      }

      /* Outfit font classes - override with heading font */
      .font-outfitBlack, .font-outfitBold, .font-outfitSemiBold,
      .font-outfitMedium, .font-outfitExtraBold {
        font-family: var(--font-heading) !important;
      }

      /* Links in navbar specifically */
      nav a {
        font-family: var(--font-heading) !important;
      }

      /* Button Color Styles */
      /* Primary Button */
      .btn-primary {
        background-color: var(--btn-primary-bg);
        color: var(--btn-primary-text);
        transition: all 0.3s ease;
        border: none;
        cursor: pointer;
      }

      .btn-primary:hover {
        background-color: var(--btn-primary-hover-bg);
        color: var(--btn-primary-hover-text);
      }

      .btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      /* Secondary Button */
      .btn-secondary {
        background-color: var(--btn-secondary-bg);
        color: var(--btn-secondary-text);
        transition: all 0.3s ease;
        border: none;
        cursor: pointer;
      }

      .btn-secondary:hover {
        background-color: var(--btn-secondary-hover-bg);
        color: var(--btn-secondary-hover-text);
      }

      .btn-secondary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      /* Tertiary Button */
      .btn-tertiary {
        background-color: var(--btn-tertiary-bg);
        color: var(--btn-tertiary-text);
        transition: all 0.3s ease;
        border: 1px solid var(--btn-tertiary-text);
        cursor: pointer;
      }

      .btn-tertiary:hover {
        background-color: var(--btn-tertiary-hover-bg);
        color: var(--btn-tertiary-hover-text);
      }

      .btn-tertiary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      /* Navbar Color Styles */
      nav {
        background-color: var(--navbar-bg) !important;
      }

      nav a:not(.btn-primary):not(.btn-secondary):not(.btn-tertiary),
      nav button:not(.btn-primary):not(.btn-secondary):not(.btn-tertiary),
      nav [role="button"]:not(.btn-primary):not(.btn-secondary):not(.btn-tertiary) {
        color: var(--navbar-link-color) !important;
        transition: color 0.3s ease;
      }

      nav a:not(.btn-primary):not(.btn-secondary):not(.btn-tertiary):hover,
      nav button:not(.btn-primary):not(.btn-secondary):not(.btn-tertiary):hover,
      nav [role="button"]:not(.btn-primary):not(.btn-secondary):not(.btn-tertiary):hover {
        color: var(--navbar-link-hover-color) !important;
      }

      /* Mobile menu / Drawer styling */
      [class*="drawer"], [class*="Drawer"],
      .mobile-menu, .nav-menu {
        background-color: var(--navbar-mobile-bg) !important;
      }

      [class*="drawer"] a, [class*="Drawer"] a,
      [class*="drawer"] button, [class*="Drawer"] button,
      .mobile-menu a, .nav-menu a {
        color: var(--navbar-mobile-text) !important;
      }

      [class*="drawer"] a:hover, [class*="Drawer"] a:hover,
      [class*="drawer"] button:hover, [class*="Drawer"] button:hover,
      .mobile-menu a:hover, .nav-menu a:hover {
        color: var(--navbar-mobile-hover) !important;
      }
    `;

    // Remove existing style if it exists
    const existingStyle = document.getElementById('font-config-styles');
    if (existingStyle) {
      existingStyle.remove();
    }

    document.head.appendChild(style);
  }, [themeConfig]);
};

export default useFontConfig;
